import DocumentInfoModal from './DocumentInfoModal';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import { 
  Calendar, 
  Target, 
  Rocket, 
  Users as UsersIcon, 
  BarChart,
  Eye,
  Shield,
  MessageSquare,
  Zap,
  Star
} from 'lucide-react';

interface Roadmap24ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Roadmap24Modal({ isOpen, onClose }: Roadmap24ModalProps) {
  const docType = DOCUMENT_TYPES.find(d => d.code === 'ROADMAP_24M')!;

  const config = {
    esprit: {
      heroTitle: "L'Esprit de la Feuille de Route TI 12-24 mois",
      heroDescription: "La Feuille de Route TI est le <strong>calendrier d'exécution tactique</strong> qui transforme la vision stratégique du Plan Directeur en <strong>actions concrètes</strong> pour les 12-24 prochains mois. C'est le pont entre la stratégie (3-5 ans) et l'opérationnel (trimestres).",
      pourquoi: {
        probleme: "Sans feuille de route claire, les projets TI sont <strong>décousus</strong>, les priorités floues, les ressources mal allouées. On réagit aux urgences au lieu d'agir selon un plan.",
        solution: "La Feuille de Route établit un <strong>calendrier structuré</strong> par trimestres, aligne les initiatives avec les objectifs d'affaires, et permet de suivre la progression vers les objectifs stratégiques."
      },
      analogie: {
        title: "Une Analogie Simple",
        content: "Si le Plan Directeur est le <strong>plan d'architecte</strong> de la maison, la Feuille de Route est le <strong>calendrier de construction</strong> qui dit:",
        points: [
          "Q1: Fondations et structure (infrastructure de base)",
          "Q2: Plomberie et électricité (sécurité et réseau)",
          "Q3: Finitions intérieures (applications et données)",
          "Q4: Aménagement et optimisation (gouvernance et amélioration continue)"
        ],
        conclusion: "La Feuille de Route TI, c'est exactement ça: le <strong>calendrier trimestriel</strong> qui transforme la vision en actions concrètes."
      },
      piliers: [
        { icon: Calendar, title: '1. Planification Trimestrielle', desc: 'Initiatives organisées par trimestres avec jalons clairs', color: 'blue' },
        { icon: Target, title: '2. Alignement Stratégique', desc: 'Chaque initiative liée aux objectifs du Plan Directeur', color: 'indigo' },
        { icon: Rocket, title: '3. Priorisation', desc: 'Initiatives classées par impact et urgence', color: 'emerald' },
        { icon: UsersIcon, title: '4. Ressources & Responsabilités', desc: 'Qui fait quoi, avec quelles ressources', color: 'purple' },
        { icon: BarChart, title: '5. Suivi & Mesure', desc: 'KPIs et jalons pour mesurer la progression', color: 'orange' },
      ],
      prismeAdvantages: [
        { title: 'Dérivée du Plan Directeur', desc: 'S\'appuie directement sur la vision stratégique établie, garantissant la cohérence' },
        { title: 'Structurée par piliers TI', desc: 'Organisée selon les 7 piliers BZ pour une vue complète et équilibrée' },
        { title: 'Calendrier réaliste', desc: 'Prend en compte les dépendances, ressources et contraintes réelles' },
        { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan détaillé avec scénarios (Niveau 5)' },
      ],
    },
    qui: {
      heroTitle: "À qui livrer la Feuille de Route TI?",
      heroDescription: "La Feuille de Route s'adresse principalement aux <strong>équipes TI</strong> et à la <strong>direction</strong> qui doivent exécuter et suivre les initiatives stratégiques.",
      profils: [
        {
          title: 'Équipe TI Interne',
          icon: UsersIcon,
          color: 'blue',
          scenarios: [
            'Planification des projets et ressources',
            'Coordination avec les fournisseurs',
            'Suivi des jalons et livrables',
            'Communication avec la direction'
          ],
          niveau: 'Niveau 3-4 recommandé'
        },
        {
          title: 'DTI / Responsable TI',
          icon: Target,
          color: 'indigo',
          scenarios: [
            'Présentation à la direction',
            'Justification des budgets et ressources',
            'Gouvernance et suivi des projets',
            'Alignement avec les objectifs stratégiques'
          ],
          niveau: 'Niveau 3-4 recommandé'
        },
        {
          title: 'Direction Générale',
          icon: Star,
          color: 'purple',
          scenarios: [
            'Vue d\'ensemble des initiatives TI',
            'Compréhension des investissements',
            'Suivi de la progression stratégique',
            'Décisions de priorisation'
          ],
          niveau: 'Niveau 2-3 recommandé'
        },
        {
          title: 'Comité TI / Gouvernance',
          icon: Shield,
          color: 'emerald',
          scenarios: [
            'Validation des priorités',
            'Approbation des budgets',
            'Suivi des KPIs et jalons',
            'Décisions stratégiques'
          ],
          niveau: 'Niveau 4-5 recommandé'
        }
      ],
      personas: [
        { role: 'DTI / Responsable TI', focus: 'Exécution, ressources, jalons', icon: Zap },
        { role: 'Direction Générale', focus: 'Vue d\'ensemble, ROI, progression', icon: Eye },
        { role: 'Équipe Projets', focus: 'Détails techniques, dépendances, planning', icon: Calendar },
      ],
    },
    quand: {
      heroTitle: "Quand et pourquoi le client en a besoin?",
      heroDescription: "La Feuille de Route est nécessaire lorsque le client a un <strong>Plan Directeur</strong> et doit maintenant <strong>passer à l'action</strong> de manière structurée.",
      questions: [
        {
          question: 'Avez-vous un Plan Directeur TI mais pas de plan d\'exécution clair?',
          siNon: '→ La Feuille de Route transforme la vision en actions concrètes'
        },
        {
          question: 'Vos projets TI sont-ils souvent en retard ou mal priorisés?',
          siNon: '→ La Feuille de Route établit un calendrier réaliste et des priorités claires'
        },
        {
          question: 'Avez-vous de la difficulté à justifier vos budgets TI trimestriels?',
          siNon: '→ La Feuille de Route lie chaque investissement à un objectif stratégique'
        },
        {
          question: 'Votre équipe TI manque-t-elle de visibilité sur les prochains projets?',
          siNon: '→ La Feuille de Route donne une vision claire des 12-24 prochains mois'
        },
        {
          question: 'Avez-vous besoin de coordonner plusieurs projets TI simultanément?',
          siNon: '→ La Feuille de Route montre les dépendances et optimise les ressources'
        },
        {
          question: 'Votre direction demande-t-elle régulièrement "où en sommes-nous"?',
          siNon: '→ La Feuille de Route fournit un outil de suivi et de communication'
        }
      ],
      signaux: [
        'Projets TI en retard ou dépassant le budget',
        'Manque de visibilité sur les priorités',
        'Ressources TI mal allouées',
        'Direction demande un plan d\'exécution',
        'Plusieurs projets TI en parallèle sans coordination',
        'Difficulté à justifier les budgets trimestriels',
        'Plan Directeur existe mais pas de plan d\'action'
      ],
      timing: [
        { timing: 'Après un Plan Directeur', raison: 'Pour transformer la vision en plan d\'action concret' },
        { timing: 'Début d\'année fiscale', raison: 'Pour planifier les initiatives de l\'année' },
        { timing: 'Lors d\'une réorganisation TI', raison: 'Pour structurer les nouvelles priorités' },
        { timing: 'Avant un budget TI annuel', raison: 'Pour justifier les investissements par trimestre' },
        { timing: 'Lors d\'une croissance rapide', raison: 'Pour gérer l\'expansion TI de manière structurée' }
      ],
    },
    valeur: {
      heroTitle: "Comment expliquer la valeur au client?",
      heroDescription: "La Feuille de Route TI n'est pas juste un calendrier, c'est un <strong>outil de gouvernance</strong> qui transforme la stratégie en résultats mesurables.",
      benefices: [
        {
          benefice: 'Visibilité 12-24 mois',
          detail: 'Savoir exactement quels projets seront lancés, quand, et avec quelles ressources',
          icon: Eye,
          color: 'indigo'
        },
        {
          benefice: 'Priorisation claire',
          detail: 'Initiatives classées par impact et urgence, évitant les décisions réactives',
          icon: Target,
          color: 'blue'
        },
        {
          benefice: 'Optimisation des ressources',
          detail: 'Allocation efficace des ressources TI selon les priorités stratégiques',
          icon: UsersIcon,
          color: 'purple'
        },
        {
          benefice: 'Réduction des risques',
          detail: 'Identification des dépendances et risques projet avant qu\'ils ne deviennent des crises',
          icon: Shield,
          color: 'orange'
        },
        {
          benefice: 'Communication efficace',
          detail: 'Outil de communication avec la direction et les parties prenantes',
          icon: MessageSquare,
          color: 'emerald'
        },
        {
          benefice: 'Suivi de progression',
          detail: 'Jalons et KPIs pour mesurer l\'avancement vers les objectifs stratégiques',
          icon: BarChart,
          color: 'pink'
        }
      ],
      roi: {
        title: 'Retour sur investissement (ROI)',
        content: 'Une Feuille de Route bien structurée peut générer un ROI de 200-400% en:',
        points: [
          { label: 'Réduction des retards projet', value: 'Éviter les dépassements de budget = économie de 30-50K$/an' },
          { label: 'Optimisation des ressources', value: 'Allocation efficace = gain d\'efficacité de 20-30%' },
          { label: 'Réduction des urgences', value: 'Planification proactive vs réactive = économie de 40-60K$/an' },
          { label: 'Meilleure coordination', value: 'Éviter les conflits de ressources = gain de temps de 15-25%' }
        ],
        cout: 'Coût d\'une Feuille de Route PRIsME: 4-12K$ (selon niveau)',
        valeur: 'Valeur générée: 40-120K$+ en économies et efficacité'
      },
      arguments: [
        {
          persona: 'Pour la Direction Générale',
          arguments: [
            'Vue claire des investissements TI par trimestre',
            'Lien direct entre projets et objectifs stratégiques',
            'Outil de suivi de la progression',
            'Justification des budgets trimestriels'
          ]
        },
        {
          persona: 'Pour le DTI / Responsable TI',
          arguments: [
            'Plan d\'exécution structuré et réaliste',
            'Allocation optimale des ressources',
            'Outil de communication avec la direction',
            'Gestion proactive des risques projet'
          ]
        },
        {
          persona: 'Pour l\'Équipe TI',
          arguments: [
            'Visibilité sur les projets à venir',
            'Clarté sur les priorités et responsabilités',
            'Planification réaliste des ressources',
            'Réduction du stress et des urgences'
          ]
        }
      ],
    },
    niveaux: {
      heroTitle: "Les 5 Niveaux de Qualité",
      heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Feuille de Route, du résumé trimestriel au plan détaillé avec scénarios. Chaque niveau s'adapte à un besoin spécifique.",
      comparisonTable: [
        { critere: 'Pages (approximatif)', values: ['2-4', '6-10', '15-25', '30-45', '50-70'] },
        { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Direction + Équipe TI', 'Comité TI', 'Gouvernance complète'] },
        { critere: 'Détail initiatives', values: ['Liste simple', 'Descriptions basiques', 'Détails moyens', 'Très détaillées', 'Complètes avec scénarios'] },
        { critere: 'Calendrier', values: ['Trimestres macro', 'Trimestres + jalons', 'Trimestres détaillés', 'Mois + dépendances', 'Semaines + ressources'] },
        { critere: 'Ressources', values: ['Estimations globales', 'Par initiative', 'Détaillées par initiative', 'Très détaillées + allocation', 'Complètes + scénarios'] },
        { critere: 'Risques & Dépendances', values: ['Mentionnées', 'Liste simple', 'Analyse basique', 'Analyse complète', 'Analyse stratégique + mitigation'] },
        { critere: 'KPIs & Suivi', values: ['Jalons principaux', 'KPIs basiques', 'KPIs par initiative', 'KPIs détaillés + dashboard', 'KPIs exécutifs + scénarios'] },
        { critere: 'Prix indicatif', values: ['4-6K$', '6-9K$', '9-15K$', '15-22K$', '22-30K$'] },
      ],
      niveauDetails: [
        {
          inclus: [
            'Résumé exécutif (1-2 pages)',
            'Initiatives principales par trimestre',
            'Calendrier macro 12 mois',
            'Jalons principaux',
            'Langage simple, accessible'
          ],
          quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, direction non technique.'
        },
        {
          inclus: [
            'Résumé exécutif + sections standardisées',
            'Initiatives détaillées par trimestre',
            'Calendrier 12-24 mois avec jalons',
            'Ressources estimées par initiative',
            'Risques et dépendances identifiés',
            'Prochaines étapes 3-6 mois'
          ],
          quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la planification TI.'
        },
        {
          inclus: [
            'Tout le niveau 2, PLUS:',
            'Initiatives très détaillées avec objectifs',
            'Calendrier détaillé par mois',
            'Ressources détaillées par initiative',
            'Dépendances entre projets',
            'KPIs par initiative',
            'Intégration ITIL/COBIT'
          ],
          quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter budget et projets, équipe TI formelle.'
        },
        {
          inclus: [
            'Tout le niveau 3, PLUS:',
            'Allocation détaillée des ressources',
            'Gestion des risques projet',
            'Plan de mitigation',
            'KPIs détaillés + dashboard',
            'Gouvernance et suivi structuré',
            'Ton très professionnel, comité-ready'
          ],
          quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
        },
        {
          inclus: [
            'Tout le niveau 4, PLUS:',
            'Scénarios (conservateur, ambitieux)',
            'Planification par semaines',
            'Optimisation des ressources',
            'ROI estimé par initiative',
            'Benchmarks sectoriels',
            'Document gouvernance-ready',
            'Ton exécutif, très visuel'
          ],
          quandUtiliser: 'Pour grandes entreprises, transformation majeure, gouvernance complète, présentation CA.'
        },
      ],
    },
  };

  const mockContext = `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Enjeux TI: vieillissement des serveurs, sauvegardes fragiles, cybersécurité minimale, irritants utilisateurs (lenteur, instabilité).
Opportunités: migration progressive vers le cloud, modernisation des applications critiques, meilleure gouvernance TI.
Plan Directeur: Vision 3-5 ans établie, besoin de plan d'exécution 12-24 mois.`;

  return (
    <DocumentInfoModal
      isOpen={isOpen}
      onClose={onClose}
      documentType={docType}
      contentConfig={config}
      mockContext={mockContext}
    />
  );
}

